import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "../components/ui/dialog";
import { HandshakeIcon, UsersThree, ChatCircleText, MapPin, Crown, PaperPlaneTilt, PlusCircle, X, Target, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const isPremium = (user) => user?.subscription_tier === "pro" || user?.role === "admin";

const MatchCard = ({ match, onConnect, premium }) => {
  // Color-code the score badge so users can visually read match strength at a glance.
  // Green ≥60 (strong), Amber 40–59 (worth exploring), Zinc <40 (weak — barely worth listing).
  const s = match.match_score || 0;
  const scoreColor = s >= 60 ? "text-emerald-400" : s >= 40 ? "text-amber-400" : "text-zinc-400";
  const scoreLabel = s >= 60 ? "Strong Match" : s >= 40 ? "Worth Exploring" : "Weak Match";
  return (
  <Card className="bg-zinc-950 border-zinc-800 hover:border-emerald-500/40 transition-colors" data-testid={`match-card-${match.user_id}`}>
    <CardContent className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-bold text-white truncate" data-testid={`match-org-${match.user_id}`}>{match.org_name}</h3>
          <p className="text-xs text-zinc-500 truncate">{match.contact_name}</p>
        </div>
        <div className="text-right flex-shrink-0">
          <div className={`text-2xl font-bold leading-none ${scoreColor}`} data-testid={`match-score-${match.user_id}`}>{match.match_score}%</div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-500">{scoreLabel}</div>
        </div>
      </div>
      {match.mission && (
        <p className="text-sm text-zinc-400 line-clamp-3">{match.mission}</p>
      )}
      <div className="flex flex-wrap gap-1.5 text-xs">
        {match.county && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-900 border border-zinc-800 rounded-sm text-zinc-400">
            <MapPin className="w-3 h-3" /> {match.county}
          </span>
        )}
        {(match.focus_areas || []).slice(0, 4).map((fa) => (
          <span key={fa} className="px-2 py-0.5 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-sm">{fa}</span>
        ))}
      </div>
      {(match.match_reasons || []).length > 0 && (
        <div className="border-t border-zinc-800 pt-2 space-y-0.5">
          {match.match_reasons.slice(0, 3).map((r, i) => (
            <p key={i} className="text-xs text-zinc-500">• {r}</p>
          ))}
        </div>
      )}
      <Button
        onClick={() => onConnect(match)}
        className={premium ? "w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" : "w-full bg-zinc-800 text-zinc-300 hover:bg-zinc-700 rounded-sm"}
        data-testid={`connect-btn-${match.user_id}`}
      >
        {premium ? <><HandshakeIcon className="w-4 h-4 mr-2" /> Send Connect Request</> : <><Crown className="w-4 h-4 mr-2 text-yellow-400" /> Premium to Connect</>}
      </Button>
    </CardContent>
  </Card>
  );
};

const Collaborate = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const grantIdFilter = searchParams.get("grant_id") || "";
  const premium = isPremium(user);

  const [activeTab, setActiveTab] = useState("matches");
  const [matches, setMatches] = useState([]);
  const [connections, setConnections] = useState({ sent: [], received: [] });
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [profileHint, setProfileHint] = useState("");
  const [grantFilterMeta, setGrantFilterMeta] = useState(null); // { grant_id, grant_title, grant_funder, total_matches }

  // Connect dialog
  const [connectTarget, setConnectTarget] = useState(null);
  const [connectMsg, setConnectMsg] = useState("");

  // Messages dialog
  const [msgConn, setMsgConn] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");

  // Create group dialog
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: "", description: "", target_grant_title: "", invite_ids: [] });

  const loadMatches = useCallback(async () => {
    setLoading(true);
    try {
      if (grantIdFilter) {
        // Grant-filtered view: only orgs whose profile matches THIS grant.
        // Powered by the Partnership → Coalition widget on Grant Detail pages.
        const res = await axios.get(`${API}/grants/${grantIdFilter}/partner-matches`, { params: { limit: 50 } });
        setMatches(res.data.matches || []);
        setGrantFilterMeta({
          grant_id: res.data.grant_id,
          grant_title: res.data.grant_title,
          grant_funder: res.data.grant_funder,
          total_matches: res.data.total_matches,
        });
        setProfileHint(res.data.has_my_profile ? "" : "Complete your nonprofit profile to see how well YOU match this grant.");
      } else {
        const res = await axios.get(`${API}/collaboration/matches`);
        setMatches(res.data.matches || []);
        setGrantFilterMeta(null);
        setProfileHint(res.data.message || "");
      }
    } catch (e) {
      toast.error("Failed to load matches");
    } finally {
      setLoading(false);
    }
  }, [grantIdFilter]);

  const clearGrantFilter = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("grant_id");
    setSearchParams(newParams);
  };

  const loadConnections = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/collaboration/connections`);
      setConnections(res.data);
    } catch (e) { /* ignore */ }
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/collaboration/groups`);
      setGroups(res.data.groups || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    loadMatches();
    loadConnections();
    loadGroups();
  }, [loadMatches, loadConnections, loadGroups]);

  const handleConnect = async () => {
    if (!premium) {
      toast("Premium required", { description: "Upgrade to send collaboration requests.", action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      return;
    }
    if (!connectTarget) return;
    try {
      await axios.post(`${API}/collaboration/connect`, {
        to_user_id: connectTarget.user_id,
        message: connectMsg,
      });
      toast.success(`Connection request sent to ${connectTarget.org_name}`);
      setConnectTarget(null);
      setConnectMsg("");
      loadConnections();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send request");
    }
  };

  const respondConnection = async (conn, action) => {
    if (action === "accept" && !premium) {
      toast("Premium required", { description: "Upgrade to accept collaboration requests.", action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      return;
    }
    try {
      await axios.put(`${API}/collaboration/connections/${conn.connection_id}/respond`, { action });
      toast.success(`Connection ${action}ed`);
      loadConnections();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const openMessages = async (conn) => {
    setMsgConn(conn);
    try {
      const res = await axios.get(`${API}/collaboration/messages`, { params: { connection_id: conn.connection_id } });
      setMessages(res.data.messages || []);
    } catch (e) {
      toast.error("Failed to load messages");
    }
  };

  const sendMessage = async () => {
    if (!premium) {
      toast("Premium required", { description: "Upgrade to send messages.", action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      return;
    }
    const text = (msgText || "").trim();
    if (!text) return;
    try {
      const res = await axios.post(`${API}/collaboration/messages`, { connection_id: msgConn.connection_id, content: text });
      setMessages((prev) => [...prev, res.data]);
      setMsgText("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
  };

  const createGroup = async () => {
    if (!premium) {
      toast("Premium required", { description: "Upgrade to create joint application groups.", action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      return;
    }
    if (!groupForm.name.trim()) {
      toast.error("Group name required");
      return;
    }
    try {
      await axios.post(`${API}/collaboration/groups`, {
        name: groupForm.name,
        description: groupForm.description,
        target_grant_title: groupForm.target_grant_title,
        invite_user_ids: groupForm.invite_ids,
      });
      toast.success("Group created");
      setShowGroupDialog(false);
      setGroupForm({ name: "", description: "", target_grant_title: "", invite_ids: [] });
      loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create group");
    }
  };

  const respondGroup = async (group, action) => {
    if (action === "accept" && !premium) {
      toast("Premium required", { description: "Upgrade to accept group invites.", action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      return;
    }
    try {
      await axios.put(`${API}/collaboration/groups/${group.group_id}/respond`, { action });
      toast.success(`Invite ${action}ed`);
      loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const toggleInvite = (uid) => {
    setGroupForm((prev) => ({
      ...prev,
      invite_ids: prev.invite_ids.includes(uid)
        ? prev.invite_ids.filter((x) => x !== uid)
        : [...prev.invite_ids, uid],
    }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="collaborate-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <HandshakeIcon weight="duotone" className="w-8 h-8 text-emerald-400" />
              Find Partners
            </h1>
            <p className="text-zinc-400 mt-1 text-sm">
              Discover SC nonprofits with similar missions. Once you've found the right partners, take it to the next level →{" "}
              <Link to="/coalitions" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2 font-semibold" data-testid="link-to-coalitions">formalize a Coalition</Link>{" "}
              to apply for grants together.
            </p>
          </div>
          {!premium && (
            <Button
              onClick={() => navigate("/pricing")}
              className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
              data-testid="collab-upgrade-btn"
            >
              <Crown className="w-4 h-4 mr-2" /> Upgrade to Connect
            </Button>
          )}
        </div>

        {/* Grant-scoped filter banner — active when navigated here from a Grant Detail widget */}
        {grantFilterMeta && (
          <Card className="bg-emerald-500/5 border border-emerald-500/40" data-testid="grant-filter-banner">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Target weight="fill" className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <p className="text-[10px] uppercase tracking-widest font-bold text-emerald-300">
                    Filtered · Partnership candidates for one grant
                  </p>
                </div>
                <p className="text-sm text-white truncate" data-testid="grant-filter-title">
                  <span className="font-bold">{grantFilterMeta.grant_title || "This grant"}</span>
                  {grantFilterMeta.grant_funder && <span className="text-zinc-400"> · {grantFilterMeta.grant_funder}</span>}
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {grantFilterMeta.total_matches} SC nonprofit{grantFilterMeta.total_matches === 1 ? "" : "s"} match{grantFilterMeta.total_matches === 1 ? "es" : ""} this grant. Connect → then formalize a Coalition to co-apply.
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button
                  onClick={() => navigate(`/coalitions?grant_id=${grantFilterMeta.grant_id}`)}
                  className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-sm text-xs"
                  data-testid="banner-to-coalition-btn"
                >
                  Formalize Coalition <ArrowRight className="w-3 h-3 ml-1" />
                </Button>
                <Button
                  onClick={clearGrantFilter}
                  variant="ghost"
                  className="text-zinc-400 hover:text-white rounded-sm text-xs"
                  data-testid="clear-grant-filter-btn"
                >
                  <X className="w-3 h-3 mr-1" /> Clear
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 border-b border-zinc-800 pb-2 overflow-x-auto">
          <Button
            variant="ghost"
            onClick={() => setActiveTab("matches")}
            className={activeTab === "matches" ? "text-emerald-400" : "text-zinc-400"}
            data-testid="tab-matches"
          >
            <UsersThree className="w-4 h-4 mr-2" /> Discover Matches {matches.length > 0 && `(${matches.length})`}
          </Button>
          <Button
            variant="ghost"
            onClick={() => setActiveTab("connections")}
            className={activeTab === "connections" ? "text-emerald-400" : "text-zinc-400"}
            data-testid="tab-connections"
          >
            <ChatCircleText className="w-4 h-4 mr-2" /> Connections {(connections.received.filter(c => c.status === "pending").length > 0) && <span className="ml-1 bg-emerald-500 text-black text-[10px] px-1.5 rounded-full">{connections.received.filter(c => c.status === "pending").length}</span>}
          </Button>
          <Button
            variant="ghost"
            onClick={() => setActiveTab("groups")}
            className={activeTab === "groups" ? "text-emerald-400" : "text-zinc-400"}
            data-testid="tab-groups"
          >
            <UsersThree className="w-4 h-4 mr-2" /> Joint Groups
          </Button>
        </div>

        {/* Matches tab */}
        {activeTab === "matches" && (
          <div className="space-y-4">
            {profileHint && (
              <Card className="bg-amber-500/5 border-amber-500/30">
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <p className="text-sm text-amber-300">{profileHint}</p>
                  <Button onClick={() => navigate("/profile")} variant="outline" className="border-amber-500/40 text-amber-300 rounded-sm" data-testid="complete-profile-btn">Complete Profile</Button>
                </CardContent>
              </Card>
            )}
            {loading ? (
              <p className="text-zinc-500 text-sm">Finding matches…</p>
            ) : matches.length === 0 ? (
              <div className="space-y-4">
                <Card className="bg-gradient-to-br from-emerald-500/5 to-zinc-950 border-emerald-500/20">
                  <CardContent className="p-8 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                        <UsersThree className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold">You&apos;re early — in a good way.</h3>
                        <p className="text-xs text-zinc-500">No matches for your profile yet. Here&apos;s how Cross-Collab works.</p>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3 text-xs">
                      <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                        <div className="text-emerald-400 font-bold mb-1">01 · Match</div>
                        <p className="text-zinc-400">We surface SC nonprofits with overlapping mission, focus areas, and geography — scored 0–100 for fit.</p>
                      </div>
                      <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                        <div className="text-emerald-400 font-bold mb-1">02 · Connect</div>
                        <p className="text-zinc-400">Send a warm intro request. When they accept, direct messaging opens between your orgs.</p>
                      </div>
                      <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                        <div className="text-emerald-400 font-bold mb-1">03 · Collaborate</div>
                        <p className="text-zinc-400">Co-apply for grants together. Share resources. Build coalitions your funders will fund.</p>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-zinc-800 flex flex-col sm:flex-row items-start sm:items-center gap-2 justify-between">
                      <p className="text-xs text-zinc-500">Two quick fixes to unlock your first matches:</p>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => navigate("/profile")} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm text-xs" data-testid="cross-collab-complete-profile">
                          Add focus areas to your profile
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <p className="text-xs text-zinc-600 text-center italic">You&apos;ll be notified the moment a matched SC organization joins the platform.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="matches-grid">
                {matches.map((m) => (
                  <MatchCard key={m.user_id} match={m} premium={premium} onConnect={(t) => { setConnectTarget(t); setConnectMsg(""); }} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Connections tab */}
        {activeTab === "connections" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-2">Received Requests</h2>
              {connections.received.length === 0 ? (
                <p className="text-sm text-zinc-500">No incoming requests yet.</p>
              ) : (
                <div className="space-y-2">
                  {connections.received.map((c) => (
                    <Card key={c.connection_id} className="bg-zinc-950 border-zinc-800" data-testid={`conn-received-${c.connection_id}`}>
                      <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between">
                        <div className="min-w-0">
                          <p className="font-semibold">{c.counterpart?.org_name || "Unknown"}</p>
                          <p className="text-xs text-zinc-500">{c.counterpart?.city} {c.counterpart?.county ? `· ${c.counterpart.county}` : ""}</p>
                          {c.initial_message && <p className="text-sm text-zinc-400 mt-1 italic">"{c.initial_message}"</p>}
                          <p className="text-xs text-zinc-600 mt-1">Status: {c.status}</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          {c.status === "pending" && (
                            <>
                              <Button size="sm" onClick={() => respondConnection(c, "accept")} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm" data-testid={`accept-btn-${c.connection_id}`}>Accept</Button>
                              <Button size="sm" variant="outline" onClick={() => respondConnection(c, "decline")} className="border-zinc-700 rounded-sm" data-testid={`decline-btn-${c.connection_id}`}>Decline</Button>
                            </>
                          )}
                          {c.status === "accepted" && (
                            <Button size="sm" onClick={() => openMessages(c)} className="bg-zinc-800 hover:bg-zinc-700 rounded-sm" data-testid={`msg-btn-${c.connection_id}`}>
                              <ChatCircleText className="w-4 h-4 mr-1" /> Message
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold mb-2">Sent Requests</h2>
              {connections.sent.length === 0 ? (
                <p className="text-sm text-zinc-500">You haven't sent any requests yet.</p>
              ) : (
                <div className="space-y-2">
                  {connections.sent.map((c) => (
                    <Card key={c.connection_id} className="bg-zinc-950 border-zinc-800" data-testid={`conn-sent-${c.connection_id}`}>
                      <CardContent className="p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between">
                        <div className="min-w-0">
                          <p className="font-semibold">{c.counterpart?.org_name || "Unknown"}</p>
                          <p className="text-xs text-zinc-500">{c.counterpart?.city} {c.counterpart?.county ? `· ${c.counterpart.county}` : ""}</p>
                          <p className="text-xs text-zinc-600 mt-1">Status: {c.status}</p>
                        </div>
                        {c.status === "accepted" && (
                          <Button size="sm" onClick={() => openMessages(c)} className="bg-zinc-800 hover:bg-zinc-700 rounded-sm" data-testid={`msg-sent-btn-${c.connection_id}`}>
                            <ChatCircleText className="w-4 h-4 mr-1" /> Message
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Groups tab */}
        {activeTab === "groups" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button
                onClick={() => setShowGroupDialog(true)}
                className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm"
                data-testid="create-group-btn"
              >
                <PlusCircle className="w-4 h-4 mr-2" /> Create Joint Application Group
              </Button>
            </div>
            {groups.length === 0 ? (
              <Card className="bg-zinc-950 border-zinc-800">
                <CardContent className="p-8 text-center text-zinc-500 space-y-2">
                  <UsersThree className="w-10 h-10 mx-auto text-zinc-700" />
                  <p className="text-sm">No groups yet.</p>
                  <p className="text-xs">Create one to coordinate on a $100K+ joint grant with other nonprofits.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {groups.map((g) => {
                  const pendingInvite = (g.pending_invite_user_ids || []).includes(user?.user_id);
                  return (
                    <Card key={g.group_id} className="bg-zinc-950 border-zinc-800" data-testid={`group-${g.group_id}`}>
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="font-bold">{g.name}</h3>
                            {g.target_grant_title && <p className="text-xs text-emerald-400">🎯 {g.target_grant_title}</p>}
                            {g.description && <p className="text-sm text-zinc-400 mt-1">{g.description}</p>}
                          </div>
                          {pendingInvite && (
                            <div className="flex gap-2 flex-shrink-0">
                              <Button size="sm" onClick={() => respondGroup(g, "accept")} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm" data-testid={`group-accept-${g.group_id}`}>Join</Button>
                              <Button size="sm" variant="outline" onClick={() => respondGroup(g, "decline")} className="border-zinc-700 rounded-sm" data-testid={`group-decline-${g.group_id}`}>Decline</Button>
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-zinc-500 flex flex-wrap gap-2 pt-1 border-t border-zinc-800">
                          <span>Members: {(g.members || []).map(m => m.org_name).join(", ") || "You"}</span>
                          {(g.pending_invites || []).length > 0 && (
                            <span>Pending: {(g.pending_invites || []).map(m => m.org_name).join(", ")}</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Connect Dialog */}
      <Dialog open={!!connectTarget} onOpenChange={(o) => !o && setConnectTarget(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg" data-testid="connect-dialog">
          <DialogHeader>
            <DialogTitle>Connect with {connectTarget?.org_name}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Send a short intro explaining why you'd like to collaborate.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="conn-msg">Message (optional)</Label>
            <Textarea
              id="conn-msg"
              value={connectMsg}
              onChange={(e) => setConnectMsg(e.target.value)}
              placeholder="Hi! I saw we both work on youth development in the Upstate. Would love to explore a joint grant."
              className="bg-zinc-900 border-zinc-800 min-h-24"
              data-testid="connect-msg-input"
              maxLength={1000}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectTarget(null)} className="border-zinc-700 rounded-sm">Cancel</Button>
            <Button onClick={handleConnect} className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" data-testid="connect-send-btn">
              <PaperPlaneTilt className="w-4 h-4 mr-2" /> Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Messages Dialog */}
      <Dialog open={!!msgConn} onOpenChange={(o) => { if (!o) { setMsgConn(null); setMessages([]); setMsgText(""); } }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl" data-testid="messages-dialog">
          <DialogHeader>
            <DialogTitle>Messages — {msgConn?.counterpart?.org_name}</DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-2 border border-zinc-800 rounded-sm p-3 bg-zinc-900" data-testid="messages-list">
            {messages.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-4">No messages yet. Say hello!</p>
            ) : (
              messages.map((m) => {
                const mine = m.from_user_id === user?.user_id;
                return (
                  <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-3 py-2 rounded-sm text-sm ${mine ? "bg-emerald-500 text-black" : "bg-zinc-800 text-white"}`} data-testid={`msg-${m.message_id}`}>
                      {m.content}
                      <div className={`text-[10px] mt-1 ${mine ? "text-black/60" : "text-zinc-400"}`}>
                        {new Date(m.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className="flex gap-2">
            <Input
              value={msgText}
              onChange={(e) => setMsgText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sendMessage(); } }}
              placeholder="Type a message…"
              className="bg-zinc-900 border-zinc-800"
              data-testid="msg-input"
            />
            <Button onClick={sendMessage} className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" data-testid="send-msg-btn">
              <PaperPlaneTilt className="w-4 h-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Group Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl" data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle>Create Joint Application Group</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Pool resources with other nonprofits to apply for larger grants together.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Group Name *</Label>
              <Input
                value={groupForm.name}
                onChange={(e) => setGroupForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Upstate Youth Coalition"
                className="bg-zinc-900 border-zinc-800"
                data-testid="group-name-input"
              />
            </div>
            <div>
              <Label>Target Grant (optional)</Label>
              <Input
                value={groupForm.target_grant_title}
                onChange={(e) => setGroupForm(p => ({ ...p, target_grant_title: e.target.value }))}
                placeholder="e.g., Mary Black Foundation — Healthy Eating & Active Living"
                className="bg-zinc-900 border-zinc-800"
                data-testid="group-grant-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={groupForm.description}
                onChange={(e) => setGroupForm(p => ({ ...p, description: e.target.value }))}
                placeholder="What is the group's mission and what role will each partner play?"
                className="bg-zinc-900 border-zinc-800 min-h-24"
                data-testid="group-desc-input"
              />
            </div>
            <div>
              <Label>Invite Nonprofits (from your matches)</Label>
              {matches.length === 0 ? (
                <p className="text-xs text-zinc-500">No matches available to invite.</p>
              ) : (
                <div className="max-h-40 overflow-y-auto border border-zinc-800 rounded-sm p-2 space-y-1">
                  {matches.map((m) => (
                    <label key={m.user_id} className="flex items-center gap-2 text-sm p-1 hover:bg-zinc-900 rounded-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={groupForm.invite_ids.includes(m.user_id)}
                        onChange={() => toggleInvite(m.user_id)}
                        data-testid={`invite-check-${m.user_id}`}
                      />
                      <span>{m.org_name}</span>
                      <span className="text-xs text-zinc-500 ml-auto">{m.match_score}% match</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupDialog(false)} className="border-zinc-700 rounded-sm">Cancel</Button>
            <Button onClick={createGroup} className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" data-testid="group-create-btn">
              Create Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Collaborate;
